<template>
  <div class="v-calendar">
    <VCalendarQuickOptions v-if="quickOptions" :quick-options @click-option="applyQuickOption" />
    <div class="v-calendar__header">
      <div class="v-calendar__current-date">
        <span v-if="precision === 'days'" class="v-calendar__current-day" @click="mode = 'days'">{{ currentDate.format('DD') }}</span>
        <span v-if="precision === 'days' || precision === 'months'" class="v-calendar__current-month" @click="mode = 'months'">{{ currentDate.format('MMMM') }}</span>
        <span class="v-calendar__current-year" @click="mode = 'years'">{{ currentDate.format('YYYY') }}</span>
        <i class="sort-caret desc" />
      </div>

      <div class="v-calendar__month-switcher">
        <VButton :debounce="false" icon="ChevronLeft" rounded size="small" type="secondary" @click="previousDate()" />
        <VButton :debounce="false" icon="ChevronRight" rounded size="small" type="secondary" @click="nextDate()" />
      </div>
    </div>

    <div class="v-calendar__body">
      <VCalendarItems :date="currentDate" :date-is-disabled :is-range="mode === 'days' && isRange" :max :min :mode :model-value="model" @update:model-value="onUpdateItem" />
    </div>

    <div class="v-calendar__footer">
      <VButton
        v-if="clearable"
        :disabled="Array.isArray(model) ? model.length === 0 || (model[0] === null && model[1] === null) : !model"
        :label="clearButtonText ?? i18n.clear"
        type="link"
        @click="onClear"
      />
      <VButton :label="confirmButtonText ?? i18n.confirm" type="link" @click="emit('confirm')" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

import type { VCalendarPrecision, VCalendarQuickOption } from '../../types.ts'
import { type Dayjs, dayjs } from '../../utils/day.ts'
import { i18nCalendar as i18n } from '../../utils/i18n.ts'
import VButton from '../VButton.vue'
import VCalendarItems, { type Mode } from './VCalendarItems.vue'
import VCalendarQuickOptions from './VCalendarQuickOptions.vue'

export type ModelValue = null | Dayjs | [Dayjs | null, Dayjs | null] | never[]

interface Props {
  isRange?: boolean
  clearable?: boolean
  quickOptions?: VCalendarQuickOption[]
  dateIsDisabled?: (date: Dayjs) => boolean
  min?: Dayjs | null
  max?: Dayjs | null
  clearButtonText?: string
  confirmButtonText?: string
  precision?: VCalendarPrecision
}

const props = withDefaults(defineProps<Props>(), {
  isRange: false,
  clearable: false,
  quickOptions: undefined,
  dateIsDisabled: () => false,
  min: null,
  max: null,
  clearButtonText: undefined,
  confirmButtonText: undefined,
  precision: 'days',
})

const model = defineModel<ModelValue>({ default: null })

const emit = defineEmits<{
  confirm: []
}>()

const currentDate = ref(dayjs())
const mode = ref<Mode>(props.precision)

function onClear() {
  if (props.isRange) {
    model.value = [null, null]
  } else {
    model.value = null
  }
}

function onUpdateItem(date: ModelValue) {
  const firstDate = Array.isArray(date) ? date[0] : date
  const secondDate = Array.isArray(date) ? date[1] : null
  const newCurrentDate = secondDate ?? firstDate

  if (mode.value === props.precision) {
    model.value = date
    if (newCurrentDate) {
      currentDate.value = newCurrentDate
    }
  } else if (newCurrentDate) {
    if (mode.value === 'months') {
      onClickMonth(newCurrentDate)
    } else {
      onClickYear(newCurrentDate)
    }
  }
}

function onClickMonth(date: Dayjs) {
  currentDate.value = date
  if (props.precision === 'days') {
    mode.value = 'days'
  }
}

function onClickYear(date: Dayjs) {
  currentDate.value = date
  if (props.precision !== 'years') {
    mode.value = 'months'
  }
}

function nextDate() {
  if (mode.value === 'days') {
    currentDate.value = currentDate.value.add(1, 'month')
  } else if (mode.value === 'months') {
    currentDate.value = currentDate.value.add(1, 'year')
  } else {
    currentDate.value = currentDate.value.add(24, 'year')
  }
}

function previousDate() {
  if (mode.value === 'days') {
    currentDate.value = currentDate.value.subtract(1, 'month')
  } else if (mode.value === 'months') {
    currentDate.value = currentDate.value.subtract(1, 'year')
  } else {
    currentDate.value = currentDate.value.subtract(24, 'year')
  }
}

function applyQuickOption(date: VCalendarQuickOption['value']) {
  if (Array.isArray(date)) {
    for (const index in date) {
      const item = date[index]
      if (item) {
        date[index] = betweenMinMax(item)
      }
    }
  } else {
    date = betweenMinMax(date)
  }

  model.value = date
  if (Array.isArray(date)) {
    if (date[0] !== null) {
      currentDate.value = date[0]
    }
  } else {
    currentDate.value = date
  }
}

function betweenMinMax(date: Dayjs) {
  if (props.min && props.min.isAfter(date)) {
    return props.min.clone()
  }

  if (props.max && props.max.isBefore(date)) {
    return props.max.clone()
  }

  return date
}
</script>

<style>
.v-calendar {
  min-width: 400px;
  max-width: 500px;
  background-color: var(--color-background);
  padding: 10px 20px 15px 20px;
}

.v-calendar__header {
  display: flex;
  align-items: center;
  padding-bottom: 7px;
  margin-bottom: 15px;
  border-bottom: var(--border);
}

.v-calendar__footer {
  padding-top: 15px;
  border-top: var(--border);
  display: flex;
  align-items: center;
  justify-content: end;
}

.v-calendar__body {
  height: 224px;
  overflow: hidden;
  display: flex;
  align-items: center;

  > div {
    width: 100%;
  }
}

.v-calendar__current-date {
  display: flex;
  align-items: center;
  flex-grow: 1;
  font-size: 20px;

  .v-calendar__current-day,
  .v-calendar__current-month,
  .v-calendar__current-year {
    cursor: pointer;
    transition:
      color var(--transition),
      text-decoration var(--transition);
    border-radius: 20px;
    padding: 0 2px;
    text-decoration: underline;
    text-decoration-color: transparent;

    &:hover {
      color: var(--color-primary);
      text-decoration-color: var(--color-primary);
    }
  }

  .sort-caret {
    transition: border-color var(--transition);
    position: relative;
    top: 2px;
    left: 5px;
  }
}

.v-calendar__month-switcher {
  display: flex;
  align-items: center;
}
</style>
